/**
 * Connection Resilience System - Types
 *
 * ISSUE_045: 24시간 켜두는 웹앱을 위한 연결 관리 시스템 타입 정의
 *
 * @module core/resilience/types
 */

// ============================================================================
// CONNECTION STATE
// ============================================================================

export type ConnectionState =
  | 'connected'      // 정상 연결
  | 'connecting'     // 연결 시도 중
  | 'reconnecting'   // 재연결 시도 중
  | 'disconnected'   // 연결 끊김
  | 'error';         // 오류 상태

export type HealthStatus =
  | 'healthy'        // 모든 시스템 정상
  | 'degraded'       // 일부 기능 저하
  | 'critical';      // 심각한 문제

// ============================================================================
// RETRY CONFIG
// ============================================================================

export interface RetryConfig {
  /** 최대 재시도 횟수 */
  maxAttempts: number;
  /** 기본 대기 시간 (ms) */
  baseDelay: number;
  /** 최대 대기 시간 (ms) */
  maxDelay: number;
  /** 백오프 승수 (예: 2 = 1s, 2s, 4s, 8s...) */
  backoffMultiplier: number;
  /** 지터 추가 여부 (랜덤 딜레이로 thundering herd 방지) */
  jitter: boolean;
}

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxAttempts: 5,
  baseDelay: 1000,
  maxDelay: 30000,
  backoffMultiplier: 2,
  jitter: true,
};

// ============================================================================
// CIRCUIT BREAKER
// ============================================================================

export type CircuitState =
  | 'closed'         // 정상 - 요청 통과
  | 'open'           // 열림 - 요청 차단
  | 'half-open';     // 반열림 - 테스트 요청 허용

export interface CircuitBreakerConfig {
  /** 회로 열림 트리거 실패 횟수 */
  failureThreshold: number;
  /** 회로 열림 유지 시간 (ms) */
  resetTimeout: number;
  /** half-open 상태에서 성공 필요 횟수 */
  successThreshold: number;
}

export const DEFAULT_CIRCUIT_CONFIG: CircuitBreakerConfig = {
  failureThreshold: 5,
  resetTimeout: 30000,
  successThreshold: 1,
};

// ============================================================================
// HEALTH CHECK
// ============================================================================

export interface HealthCheckResult {
  status: HealthStatus;
  session: {
    valid: boolean;
    expiresIn: number | null; // ms
  };
  connection: {
    state: ConnectionState;
    lastPingAt: number | null;
  };
  sync: {
    lastSyncAt: number | null;
    pendingChanges: number;
  };
  timestamp: number;
}

// ============================================================================
// EVENTS
// ============================================================================

export type ResilienceEvent =
  | { type: 'connection_lost' }
  | { type: 'connection_restored' }
  | { type: 'token_expired' }
  | { type: 'token_refreshed' }
  | { type: 'sync_failed'; error: string }
  | { type: 'sync_recovered' }
  | { type: 'circuit_opened'; reason: string }
  | { type: 'circuit_closed' }
  | { type: 'health_degraded'; checks: string[] }
  | { type: 'health_critical'; checks: string[] }
  | { type: 'health_restored' };

export type ResilienceEventHandler = (event: ResilienceEvent) => void;

// ============================================================================
// STORE STATE
// ============================================================================

export interface ResilienceState {
  // 연결 상태
  connectionState: ConnectionState;
  reconnectAttempts: number;
  lastConnectedAt: number | null;

  // 헬스 상태
  healthStatus: HealthStatus;
  lastHealthCheck: HealthCheckResult | null;

  // Circuit Breaker
  circuitState: CircuitState;
  circuitFailures: number;
  circuitOpenedAt: number | null;

  // 통계
  totalReconnects: number;
  totalTokenRefreshes: number;
  lastError: string | null;
}

export interface ResilienceActions {
  // 연결
  setConnectionState: (state: ConnectionState) => void;
  incrementReconnectAttempts: () => void;
  resetReconnectAttempts: () => void;

  // 헬스
  setHealthStatus: (status: HealthStatus) => void;
  setLastHealthCheck: (result: HealthCheckResult) => void;

  // Circuit Breaker
  recordFailure: () => void;
  recordSuccess: () => void;
  tripCircuit: () => void;
  resetCircuit: () => void;

  // 에러
  setLastError: (error: string | null) => void;

  // 통계
  incrementReconnects: () => void;
  incrementTokenRefreshes: () => void;
}
